import React, { useContext, useEffect, useState } from "react";
import moment from "moment";
import { ButtonGroup, Stack, Grid, Box } from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import Button from "components/shared/Button/Button";
import {
  Line,
  LineChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend
} from "recharts";
import { GlobalContext } from "context/Provider";
import API from "api";
import Session from "helpers/session";
import {
  associateReadingType,
  formatSpirometry,
  toLocalTime
} from "helpers/variables";
import { ReadingContextInterface } from "context/initialState/Readings";
import EncounterTimer from "components/shared/EncounterTimer/EncounterTimer";
import { useTranslation } from "react-i18next";
import { StyledResponsiveContainer } from "./Chart.styles";
import { READING_TYPES } from "helpers/appConstants";
const {
  bloodGlucose,
  bloodPressure,
  temperature,
  weight,
  heartRate,
  oxygenSaturation,
  spirometry,
  activity
} = READING_TYPES;

interface ChartProps {
  type: string;
}

interface GraphInterface {
  measuredAt: string | undefined;
  systolic?: number | undefined;
  diastolic?: number | undefined;
  glucose?: number | undefined;
  spo2?: number | undefined;
  FVC?: string | undefined;
  FEV1?: string | undefined;
  "FEV1/FVC%"?: string | undefined;
  pulse?: number | undefined;
  temp?: number | undefined;
  weight?: number | undefined;
  activity?: number | undefined;
}

let key = "";
const formatChart = (
  readings: ReadingContextInterface[],
  type: string
): GraphInterface[] => {
  let graphData: GraphInterface[] = [];
  readings.map((reading) => {
    switch (type) {
      case heartRate.display:
        graphData.push({
          measuredAt: moment(toLocalTime(reading.measuredAt)).format(
            "MMM DD HH:mm"
          ),
          pulse: reading.metrics?.pulse
        });
        break;
      case bloodPressure.display:
        graphData.push({
          measuredAt: moment(toLocalTime(reading.measuredAt)).format(
            "MMM DD HH:mm"
          ),
          systolic: reading.metrics?.systolic,
          diastolic: reading.metrics?.diastolic
        });
        break;
      case bloodGlucose.display:
        graphData.push({
          measuredAt: moment(toLocalTime(reading.measuredAt)).format(
            "MMM DD HH:mm"
          ),
          glucose: reading.metrics?.glucose
        });
        break;
      case oxygenSaturation.display:
        graphData.push({
          measuredAt: moment(toLocalTime(reading.measuredAt)).format(
            "MMM DD HH:mm"
          ),
          spo2: reading.metrics?.spo2
        });
        break;
      case temperature.display:
        graphData.push({
          measuredAt: moment(toLocalTime(reading.measuredAt)).format(
            "MMM DD HH:mm"
          ),
          temp: reading.metrics?.temp
        });
        break;
      case weight.display:
        graphData.push({
          measuredAt: moment(toLocalTime(reading.measuredAt)).format(
            "MMM DD HH:mm"
          ),
          weight: reading.metrics?.weight
        });
        break;
      case activity.display:
        graphData.push({
          measuredAt: moment(toLocalTime(reading.measuredAt)).format(
            "MMM DD HH:mm"
          ),
          activity: reading.metrics?.steps
        });
        break;
      case spirometry.display:
        const spirReading = formatSpirometry(reading);
        const { FEV1, FVC } = spirReading;
        if (spirReading.device === "Spirobank Smart") {
          key = "FEV1/FVC%";
          graphData.push({
            measuredAt: moment(toLocalTime(reading.measuredAt)).format(
              "MMM DD HH:mm"
            ),
            "FEV1/FVC%": ((Number(FEV1) / Number(FVC)) * 100).toFixed(1)
          });
        } else if (spirReading.device === "Vitalograph 40372") {
          key = "FEV1";
          graphData.push({
            measuredAt: moment(toLocalTime(reading.measuredAt)).format(
              "MMM DD HH:mm"
            ),
            FEV1: spirReading.FEV1
          });
        }

        break;
    }
  });
  return graphData;
};

const formatLine = (type: string) => {
  switch (type) {
    case bloodPressure.display:
      return (
        <>
          <Line type="monotone" dataKey="systolic" stroke="#FF0000" />
          <Line
            type="monotone"
            dataKey="diastolic"
            stroke="#0000FF"
            activeDot={{ r: 8 }}
          />
        </>
      );
    case spirometry.display:
      return <Line type="monotone" dataKey={key} stroke="#8884d8" />;
    case bloodGlucose.display:
      return <Line type="monotone" dataKey="glucose" stroke="#8884d8" />;
    case oxygenSaturation.display:
      return <Line type="monotone" dataKey="spo2" stroke="#8884d8" />;
    case temperature.display:
      return <Line type="monotone" dataKey="temp" stroke="#8884d8" />;
    case weight.display:
      return <Line type="monotone" dataKey="weight" stroke="#8884d8" />;
    //activity
    case activity.display:
      return <Line type="monotone" dataKey="activity" stroke="#8884d8" />;
    case heartRate.display:
      return <Line type="monotone" dataKey="pulse" stroke="#8884d8" />;
    case spirometry.display:
      return <Line type="monotone" dataKey={key} stroke="#8884d8" />;
  }
};

export default function Chart(props: ChartProps) {
  const today = new Date();
  const sevenDay = new Date();
  const thirtyDay = new Date();
  const ninetyDay = new Date();
  const manyDay = new Date();
  const offsets = {
    sevenDays: moment(sevenDay.setDate(sevenDay.getDate() - 7)).format(
      "YYYY-MM-DD"
    ),
    thirtyDays: moment(thirtyDay.setDate(thirtyDay.getDate() - 30)).format(
      "YYYY-MM-DD"
    ),
    sixtyDays: moment(thirtyDay.setDate(thirtyDay.getDate() - 60)).format(
      "YYYY-MM-DD"
    ),
    ninetyDays: moment(ninetyDay.setDate(ninetyDay.getDate() - 90)).format(
      "YYYY-MM-DD"
    )
  };

  const [range, setRange] = React.useState(offsets.thirtyDays);
  const [graphData, setGraphData] = useState<GraphInterface[]>([]);
  const { member } = useContext(GlobalContext);
  const { t } = useTranslation();
  const heartRateToggle = props.type !== heartRate.display;

  const handleRangeChange = (newRange: number) => {
    let chartRange = "";
    if (newRange === 0) {
      chartRange = moment(today).format("YYYY-MM-DD");
    } else if (newRange === 7) {
      chartRange = offsets.sevenDays;
    } else if (newRange === 30) {
      chartRange = offsets.thirtyDays;
    } else if (newRange === 60) {
      chartRange = offsets.sixtyDays;
    } else if (newRange === 90) {
      chartRange = offsets.ninetyDays;
    }
    return chartRender(chartRange);
  };
  const chartRender = (newRange: string) => {
    const additionalSources = heartRateToggle ? "" : "&type=spo2&type=bp";
    API.kore.readings
      .getForOne(
        member?.current.id || "",
        Session.crackCookieToken("coordinator"),
        associateReadingType(props.type),
        `${additionalSources}measured_between=${newRange},${moment(today).format(
          "YYYY-MM-DD"
        )}T00:00:00`
      )
      .then((readings) => {
        setGraphData(formatChart(readings.items, props.type));
      });
  };
  useEffect(() => {
    chartRender(range);
  }, [props.type]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box
        sx={{
          overflow: "scroll",
          position: "fixed",
          top: 250,
          right: 110,
          bottom: 68,
          left: 290,
          paddingBottom: 10
        }}
      >
        <EncounterTimer></EncounterTimer>
        <Stack
          direction="row"
          spacing={1}
          alignItems={"center"}
          justifyContent={"center"}
        >
          <ButtonGroup>
            <Button
              variant="info"
              margin={1}
              onClick={() => {
                handleRangeChange(90);
              }}
            >
              90 Days
            </Button>
            <Button
              variant="info"
              margin={1}
              onClick={() => {
                handleRangeChange(60);
              }}
            >
              60 Days
            </Button>
            <Button
              variant="info"
              margin={1}
              onClick={() => {
                handleRangeChange(30);
              }}
            >
              30 Days
            </Button>
            <Button
              variant="info"
              margin={1}
              onClick={() => {
                handleRangeChange(7);
              }}
            >
              7 Days
            </Button>
          </ButtonGroup>
        </Stack>

        <StyledResponsiveContainer width={"90%"} height={"120%"}>
          <LineChart data={graphData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="measuredAt" />
            <YAxis padding={{ top: 50 }} />
            <Tooltip />
            <Legend />
            {formatLine(props.type)}
          </LineChart>
        </StyledResponsiveContainer>
      </Box>
    </LocalizationProvider>
  );
}
